import React, { useState } from 'react';
import { View, TextInput, KeyboardTypeOptions, NativeSyntheticEvent, TextInputKeyPressEventData, ViewStyle, StyleProp, TextStyle, TextInputProps } from 'react-native';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { CustomTooltip } from './CustomTooltip';
import { Hoverable } from 'react-native-web-hover';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import inputStylesheet from '../styles/inputStyles';

type ModeType = 'TEXT' | 'NUMBER';

type FormInputProps = {
    toUppercase?: boolean;
    label?: string;
    placeholder?: string;
    errorMessage?: string;
    invalid?: (value: string) => boolean;
    disabled?: boolean;
    secureTextEntry?: boolean;
    keyboardType?: KeyboardTypeOptions;
    onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
    maxLength?: number;
    minLength?: number;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    inputStyle?: StyleProp<TextStyle>;
    multiline?: boolean;
    numberOfLines?: number;
    autoComplete?: TextInputProps['autoComplete'];
    required?: boolean;
    hintText?: string | React.ReactNode | React.ReactNode;
    hintTextConfig?: {
        tooltipStyle?: React.CSSProperties;
        tooltipDirection?: 'right' | 'left' | 'top';
    };
    /**
     * default value: true
     */
    semiBoldLabel?: boolean;
    maxCharacters?: number;
    minValue?: number;
    maxValue?: number;
    mode?: ModeType;
    mask?: (val: string) => string;
    onContentSizeChange?: (val: { nativeEvent: { contentSize: { width: number; height: number; }; }; }) => void;
    prefix?: string;
} & ({
    form?: undefined;
    fieldName?: undefined;
    customValidations?: undefined;
    pattern?: undefined;
    value: string;
    onChangeText: (value: string) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: undefined;
    onChangeText?: undefined;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
});

export default function FormInput({
    toUppercase,
    label,
    placeholder,
    errorMessage,
    value,
    onChangeText,
    invalid,
    disabled,
    secureTextEntry,
    maxLength = 255,
    minLength,
    keyboardType,
    onKeyPress,
    viewStyle,
    labelStyle,
    multiline,
    numberOfLines,
    autoComplete,
    required,
    hintText,
    hintTextConfig,
    maxCharacters,
    minValue,
    maxValue,
    mode = 'TEXT',
    form,
    fieldName,
    customValidations,
    pattern,
    mask,
    onContentSizeChange,
    prefix,
}: FormInputProps) {
    const { styles } = useStyles(stylesheet);
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);
    const [isDirty, setIsDirty] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);
    const [hintHovered, setHintHovered] = useState(false);


    disabled = !!disabled;

    const isTooShort = (v: string) => minLength !== undefined && v.length > 0 && v.length < minLength;

    if (form && fieldName) {

        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        rules.required = required ? translate('required_field') : false;

        if (pattern) {
            rules.pattern = pattern;
        }

        if (maxLength) {
            rules.maxLength = maxLength;
        }

        if (maxLength) {
            rules.maxLength = maxLength;
        }

        if (minLength) {
            rules.minLength = {
                value: minLength,
                message: translate('min_length_error', { minLength }),
            };
        }

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        if (mode == 'NUMBER' && minValue) {
            rules.min = minValue;
        }

        if (mode == 'NUMBER' && maxValue) {
            rules.max = maxValue;
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[defaultInputStyle.container, hintHovered && { zIndex: 9999 }, viewStyle]}>
                    <View style={[defaultInputStyle.labelContainer, hintText ? { zIndex: 9999 } : {}]}>
                        {label && <MyAppText style={labelStyle ? labelStyle : defaultInputStyle.label}>{label}</MyAppText>}
                        {required && label ?
                            <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                            : null}
                        {hintText
                            ? <Hoverable>
                                {({ hovered }) => {
                                    setHintHovered(hovered);
                                    return <CustomTooltip
                                        content={hintText}
                                        tooltipStyle={hintTextConfig?.tooltipStyle}
                                        tooltipDirection={hintTextConfig?.tooltipDirection}
                                    />;
                                }}
                            </Hoverable>
                            : null
                        }
                    </View>
                    <View style={prefix ? styles.inputRow : undefined}>
                        {prefix ? (
                            <View style={defaultInputStyle.inputPrefix({ invalid: Boolean(fieldState.error) })}>
                                <MyAppText style={defaultInputStyle.inputPrefixText}>{prefix}</MyAppText>
                            </View>
                        ) : null}
                        <TextInput
                            onBlur={field.onBlur}
                            autoComplete={autoComplete}
                            editable={!disabled}
                            placeholder={placeholder ? placeholder : label}
                            style={defaultInputStyle.input({
                                invalid: Boolean(fieldState.error),
                                disabled,
                                placeholder: !field.value,
                                hasPrefix: Boolean(prefix),
                                multiline,
                            })}
                            value={mask && field.value ? mask(field.value) : field.value}
                            onChangeText={(value) => {
                                if (toUppercase) {
                                    value = value.toUpperCase();
                                }
                                if (mode === 'NUMBER') {
                                    value = value.replace(/[^0-9]/g, '');
                                }
                                field.onChange(value);
                            }}
                            secureTextEntry={secureTextEntry}
                            keyboardType={keyboardType}
                            onKeyPress={onKeyPress}
                            maxLength={maxLength}
                            multiline={multiline}
                            numberOfLines={numberOfLines}
                            onContentSizeChange={onContentSizeChange}
                        />
                    </View>
                    {maxCharacters ? <MyAppText style={defaultInputStyle.maxCharactersMessage}>{translate('characters')}: {field.value.length}/{maxCharacters}</MyAppText> : null}
                    {fieldState.error ? <MyAppText style={defaultInputStyle.errorMessage}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;
    } else if (value !== undefined && onChangeText !== undefined) {
        return (
            <View style={[defaultInputStyle.container, hintHovered && { zIndex: 9999 }, viewStyle]}>
                <View style={[defaultInputStyle.labelContainer, hintText ? { zIndex: 9999 } : {}]}>
                    {label && <MyAppText style={labelStyle ? labelStyle : defaultInputStyle.label}>{label}</MyAppText>}
                    {required && label ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                    {hintText
                        ? <Hoverable>
                            {({ hovered }) => {
                                setHintHovered(hovered);
                                return <CustomTooltip
                                    content={hintText}
                                    tooltipStyle={hintTextConfig?.tooltipStyle}
                                    tooltipDirection={hintTextConfig?.tooltipDirection}
                                />;
                            }}
                        </Hoverable>
                        : null
                    }
                </View>
                <TextInput
                    autoComplete={autoComplete}
                    editable={!disabled}
                    placeholder={placeholder ? placeholder : label}
                    style={defaultInputStyle.input({
                        invalid: isInvalid,
                        disabled,
                        placeholder: value == '',
                        hasPrefix: Boolean(prefix),
                        multiline,
                    })}

                    value={mask && value ? mask(value) : value}
                    onBlur={() => {
                        if (invalid || minLength !== undefined) {
                            setIsInvalid(isDirty && ((invalid?.(value) ?? false) || isTooShort(value)));
                        }
                    }}
                    onChangeText={(value) => {
                        if (!invalid?.(value) && !isTooShort(value)) {
                            setIsInvalid(false);
                        }
                        setIsDirty(true);

                        if (mode === 'NUMBER') {
                            value = value.replace(/[^0-9]/g, '');
                            if (minValue !== undefined && Number(value) < minValue) {
                                setIsInvalid(true);
                            } else if (maxValue !== undefined && Number(value) > maxValue) {
                                setIsInvalid(true);
                            } else {
                                setIsInvalid(false);
                            }
                        }

                        if (maxCharacters) {
                            value = value.slice(0, maxCharacters);
                        }

                        if (toUppercase) {
                            value = value.toUpperCase();
                        }

                        onChangeText(value);
                    }}
                    secureTextEntry={secureTextEntry}
                    keyboardType={keyboardType}
                    onKeyPress={onKeyPress}
                    maxLength={maxLength}
                    multiline={multiline}
                    numberOfLines={numberOfLines}
                    onContentSizeChange={onContentSizeChange}
                />
                {maxCharacters ? <MyAppText style={defaultInputStyle.maxCharactersMessage}>{translate('characters')}: {value.length}/{maxCharacters}</MyAppText> : null}
                {isInvalid ?
                    <MyAppText style={defaultInputStyle.errorMessage}>
                        {
                            minValue !== undefined && Number(value) < minValue ? translate('value_bellow_min_accept', { minValue }) :
                                maxValue !== undefined && Number(value) > maxValue ? translate('value_over_max_accept', { maxValue }) :
                                    isTooShort(value) ? translate('min_length_error', { minLength }) :
                                        errorMessage ? errorMessage : translate('required_field')
                        }
                    </MyAppText>
                    : null}
            </View>
        );
    }

}

const stylesheet = createStyleSheet(() => ({
    inputRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
    },
}));
